import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { HiOutlineBell, HiXMark } from "react-icons/hi2";
import axiosInstance from "../../config/axiosInstance";

import NotificationList from "../../components/Notifications/NotificationList";


export default function NotificationIcon({notificationCount, getNotifications, notifications}) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);


    const toggleDropdown = () => {
        setIsDropdownOpen(prev => !prev);
    };

    // Close dropdown when clicking outside
    useEffect(() => {

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Prevent body scroll when panel is open
    useEffect(() => {
        if (isDropdownOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isDropdownOpen]);

    return (
        <>
            <div className="relative" ref={dropdownRef}>
                {/* Bell Icon */}
                <button 
                    className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
                    onClick={toggleDropdown}
                >
                    <HiOutlineBell className="h-5 w-5" />
                    
                    {/* Red Circle Badge with Number */}
                    {notificationCount > 0 && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs flex items-center justify-center rounded-full font-medium">
                            {notificationCount > 99 ? '99+' : notificationCount}
                        </div>
                    )}
                </button>
            </div>

            {/* Full Screen Notifications Panel - Rendered via Portal */}
            {isDropdownOpen && createPortal(
                <>
                    {/* Background Blur Overlay */}
                    <div 
                        className="fixed inset-0 bg-black/30 backdrop-blur-md z-[9998] transition-opacity duration-300"
                        onClick={toggleDropdown}
                    />
                    
                    {/* Slide-in Panel */}
                    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white dark:bg-gray-800 shadow-2xl z-[9999] transform transition-transform duration-300 ease-out">
                        <div className="flex flex-col h-full">
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Notifications</h2>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        Last updated {new Date().toLocaleDateString()}
                                    </p>
                                </div>
                                <button 
                                    onClick={toggleDropdown}
                                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                                >
                                    <HiXMark className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                                <div className="p-6">
                                    {notifications && notifications.length > 0 ? (
                                        <div className="space-y-4">
                                            <NotificationList notifications={notifications} />
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                                                <HiOutlineBell className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                                            </div>
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No notifications</h3>
                                            <p className="text-gray-500 dark:text-gray-400 text-sm">You're all caught up! New notifications will appear here.</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Footer */}
                            {notifications && notifications.length > 0 && (
                                <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                    <button className="w-full py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200">
                                        Mark all as read
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </>,
                document.body
            )}
        </>
    )
}